import { useMemo, useState } from 'react';
import { P2GBox, P2GImage, SvgLocker } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { DropLocation, QuoteServiceApiModel } from '~/lib/api/smartsend/types';
import { useServiceLogo } from '../../../../../../hooks/use-service-logo';
import { SvgEnquiryMoreInfo } from '@parcel2go/core-ui';
import { SvgPrinter } from '@parcel2go/core-ui';
import { SvgPrinterNoPrinter } from '@parcel2go/core-ui';
import { SvgVan } from '@parcel2go/core-ui';
import { SvgShop } from '@parcel2go/core-ui';
import ServiceInfo, { ServiceTabType } from '../ServiceInfo';
import { P2GServiceLogo } from '@parcel2go/core-ui';

import ServiceTags from '../ServiceTags';
import { Tagged } from '../../../Tagged';
import { P2GSidePanel } from '~/components/sidepanel';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';
import { CollectionImageName } from './QuoteCardBase';

export interface ServiceCardProps {
  availableExtras: string[];
  service: QuoteServiceApiModel;
  closestPickupLocation?: DropLocation;
  currencyCode: string;
}

export default function QuoteService(props: ServiceCardProps) {
  const [tab, setTab] = useState<ServiceTabType>('');
  const { getSrc } = useServiceLogo();

  const imageName = useMemo(() => CollectionImageName(props.service.CollectionType), [props.service.CollectionType]);

  return (
    <P2GBox>
      <P2GGrid container gap={2} alignItems="center" alignContent="center" wrap="nowrap" justify="flex-end">
        <P2GGrid item>
          <P2GImage
            style={{ height: '35px', maxWidth: '180px' }}
            src={getSrc(props.service)}
            alt={props.service?.Name}
          />
        </P2GGrid>
        <P2GGrid item flexGrow={1}></P2GGrid>
        <P2GGrid item>
          <CdnIconProxy
            size="sm"
            palette="primary"
            onClick={() => setTab('INFO')}
            src="/theme/icons/EnquiryMoreInfo.svg"
          >
            <SvgEnquiryMoreInfo />
          </CdnIconProxy>
        </P2GGrid>
        <P2GGrid item>
          {props.service.PrinterRequired ? (
            <CdnIconProxy size="sm" palette="primary" onClick={() => setTab('PRINTER')} src="/theme/icons/Printer.svg">
              <SvgPrinter />
            </CdnIconProxy>
          ) : (
            <CdnIconProxy
              size="sm"
              palette="primary"
              onClick={() => setTab('PRINTER')}
              src="/theme/icons/PrinterNoPrinter.svg"
            >
              <SvgPrinterNoPrinter />
            </CdnIconProxy>
          )}
        </P2GGrid>
        <P2GGrid item>
          <CdnIconProxy
            size="sm"
            palette="primary"
            onClick={() => setTab('DELIVERYTYPE')}
            src={`/theme/icons/${imageName}.svg`}
          >
            {imageName === 'Van' && <SvgVan />}
            {imageName === 'Locker' && <SvgLocker />}
            {imageName === 'Shop' && <SvgShop />}
          </CdnIconProxy>
        </P2GGrid>
      </P2GGrid>
      <P2GBox pt={1} sx={{ minHeight: 60, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <P2GText variant="subtitle2">{props.service.Name}</P2GText>
        <ServiceTags
          setTab={setTab}
          service={props.service}
          availableExtras={props.availableExtras}
          closestPickupLocation={props.closestPickupLocation}
        />
      </P2GBox>
      <P2GSidePanel
        open={!!tab}
        onClose={() => setTab('')}
        anchor="right"
        title={props.service.Name}
        preTitle={
          <div style={{ width: '150px' }}>
            <P2GBox p={1} bordered>
              <P2GServiceLogo size="normal-svg" slug={props.service.Id} />
            </P2GBox>
          </div>
        }
      >
        {!!tab && (
          <Tagged tag="panel-service-information">
            <ServiceInfo
              setTab={setTab}
              tab={tab}
              itemId={null}
              key={`${props.service.Id}_${props.service.Provider}_${props.service.AccountId}`}
              id={props.service.Id}
              provider={props.service.Provider}
              accountId={props.service.AccountId}
              currencyCode={props.currencyCode}
            />
          </Tagged>
        )}
      </P2GSidePanel>
    </P2GBox>
  );
}
